// Use /api for production (proxied through OpenLiteSpeed) or localhost for development
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://localhost:3002/api' 
    : '/api');

// Get auth token from localStorage
const getToken = () => {
  return localStorage.getItem('authToken');
};

// API request helper
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Auth API
export const authAPI = {
  register: (email, password, name) =>
    apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),

  login: (email, password) =>
    apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  forgotPassword: (email) =>
    apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token, password) =>
    apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
};

// Properties API
export const propertiesAPI = {
  getAll: () => apiRequest('/properties'),
  create: (property) =>
    apiRequest('/properties', {
      method: 'POST',
      body: JSON.stringify(property),
    }),
  update: (id, property) =>
    apiRequest(`/properties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(property),
    }),
  delete: (id) =>
    apiRequest(`/properties/${id}`, {
      method: 'DELETE',
    }),
};

// Guest Pages API
export const guestPagesAPI = {
  getAll: () => apiRequest('/guest-pages'),
  getBySlug: (slug) => apiRequest(`/guest-pages/${slug}`),
  create: (guestPage) =>
    apiRequest('/guest-pages', {
      method: 'POST',
      body: JSON.stringify(guestPage),
    }),
  update: (id, guestPage) =>
    apiRequest(`/guest-pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(guestPage),
    }),
  delete: (id) =>
    apiRequest(`/guest-pages/${id}`, {
      method: 'DELETE',
    }),
};

// Email API
export const emailAPI = {
  getSmtpSettings: () => apiRequest('/smtp-settings'),
  updateSmtpSettings: (settings) =>
    apiRequest('/smtp-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  testSmtpConnection: (settings) =>
    apiRequest('/smtp-settings/test', {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
  sendEmail: (emailData) =>
    apiRequest('/email/send', {
      method: 'POST',
      body: JSON.stringify(emailData),
    }),
};

// Automation Settings API
export const automationAPI = {
  getSettings: () => apiRequest('/automation-settings'),
  updateSettings: (settings) =>
    apiRequest('/automation-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
};

// Email Processing API
export const emailProcessingAPI = {
  processScheduled: () =>
    apiRequest('/email/process-scheduled', {
      method: 'POST',
    }),
  getPending: () => apiRequest('/email/pending'),
  getHistory: (guestPageId, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (guestPageId) params.append('guestPageId', guestPageId);
    return apiRequest(`/email/history?${params.toString()}`);
  },
  bulkSend: (guestPageIds, templateType) =>
    apiRequest('/email/bulk-send', {
      method: 'POST',
      body: JSON.stringify({ guestPageIds, templateType }),
    }),
};

// Communication Log API
export const communicationLogAPI = {
  getLog: (guestPageId, limit = 100, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (guestPageId) params.append('guestPageId', guestPageId);
    return apiRequest(`/communication-log?${params.toString()}`);
  },
};

// Guest Notes API
export const guestNotesAPI = {
  getNotes: (guestPageId) => apiRequest(`/guest-notes/${guestPageId}`),
  createNote: (guestPageId, note, tags = []) =>
    apiRequest('/guest-notes', {
      method: 'POST',
      body: JSON.stringify({ guestPageId, note, tags }),
    }),
  updateNote: (id, note, tags = []) =>
    apiRequest(`/guest-notes/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ note, tags }),
    }),
  deleteNote: (id) =>
    apiRequest(`/guest-notes/${id}`, {
      method: 'DELETE',
    }),
};

// Bulk Actions API
export const bulkActionsAPI = {
  bulkArchive: (guestPageIds) =>
    apiRequest('/guest-pages/bulk-archive', {
      method: 'POST',
      body: JSON.stringify({ guestPageIds }),
    }),
  bulkSendEmails: (guestPageIds, templateType) =>
    apiRequest('/email/bulk-send', {
      method: 'POST',
      body: JSON.stringify({ guestPageIds, templateType }),
    }),
};

// Analytics API
export const analyticsAPI = {
  getEmailStats: (days = 30) =>
    apiRequest(`/analytics/email-stats?days=${days}`),
};

// Email Templates API
export const emailTemplatesAPI = {
  getAll: () => apiRequest('/email-templates'),
  update: (templateType, templateData) =>
    apiRequest('/email-templates', {
      method: 'PUT',
      body: JSON.stringify({ templateType, ...templateData }),
    }),
  test: (templateType, testEmail) =>
    apiRequest('/email/template/test', {
      method: 'POST',
      body: JSON.stringify({ templateType, testEmail }),
    }),
};

// Guest Email API
export const guestEmailAPI = {
  send: (guestPageId, templateType) =>
    apiRequest('/email/guest/send', {
      method: 'POST',
      body: JSON.stringify({ guestPageId, templateType }),
    }),
};

// Email Sequences API
export const emailSequencesAPI = {
  getAll: () => apiRequest('/email-sequences'),
  create: (sequence) =>
    apiRequest('/email-sequences', {
      method: 'POST',
      body: JSON.stringify(sequence),
    }),
  delete: (id) =>
    apiRequest(`/email-sequences/${id}`, {
      method: 'DELETE',
    }),
};

// AI Chat API (no auth required for guest portal)
export const aiChatAPI = {
  sendMessage: (slug, message, conversationHistory = []) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 
      (window.location.hostname === 'localhost' 
        ? 'http://localhost:3002/api' 
        : '/api');
    
    return fetch(`${API_BASE_URL}/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ slug, message, conversationHistory }),
    }).then(async (response) => {
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },
};

